import { useState, useEffect, useCallback } from 'react';
import { auth } from '../lib/firebase';
import {
  subscribeToConversations,
  subscribeToMessages,
  sendMessage,
  markMessagesAsRead,
  createConversation,
  getTotalUnreadCount,
  searchConversations
} from '../services/messaging';
import type { Conversation, Message } from '../services/messaging';

// Hook for managing user's conversations
export const useConversations = (userId: string | undefined) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      setConversations([]); // Clear conversations if no user
      return;
    }

    setLoading(true);
    const unsubscribe = subscribeToConversations(
      userId,
      (updatedConversations) => {
        setConversations(updatedConversations);
        setLoading(false);
        setError(null);
      }
    );

    return unsubscribe;
  }, [userId]);

  const searchUserConversations = useCallback(async (searchTerm: string) => {
    const currentUser = auth.currentUser;
    if (!currentUser) return [];

    try {
      const results = await searchConversations(currentUser.uid, searchTerm);
      return results;
    } catch (err) {
      setError('Failed to search conversations');
      return [];
    }
  }, []);

  return {
    conversations,
    loading,
    error,
    searchConversations: searchUserConversations
  };
};

// Hook for managing messages in a specific conversation
export const useMessages = (conversationId: string | null) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribe = subscribeToMessages(
      conversationId,
      (updatedMessages) => {
        setMessages(updatedMessages);
        setLoading(false);
        setError(null);
      }
    );

    return unsubscribe;
  }, [conversationId]);

  const sendNewMessage = useCallback(async (content: string, type: 'text' | 'image' | 'audio' = 'text', replyToId?: string | null) => {
    if (!conversationId || !content.trim()) return;

    setSending(true);
    try {
      await sendMessage(conversationId, content.trim(), type, replyToId);
      setError(null);
    } catch (err) {
      setError('Failed to send message');
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  }, [conversationId]);

  const markAsRead = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!conversationId || !currentUser) return;

    try {
      await markMessagesAsRead(conversationId, currentUser.uid);
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  }, [conversationId]);

  return {
    messages,
    loading,
    error,
    sending,
    sendMessage: sendNewMessage,
    markAsRead
  };
};

// Hook for managing unread message count
export const useUnreadCount = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const fetchUnreadCount = async () => {
      try {
        const count = await getTotalUnreadCount(currentUser.uid);
        setUnreadCount(count);
      } catch (err) {
        console.error('Error fetching unread count:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUnreadCount();

    // Set up interval to periodically check unread count
    const interval = setInterval(fetchUnreadCount, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  return { unreadCount, loading };
};

// Hook for creating new conversations
export const useCreateConversation = () => {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createNewConversation = useCallback(async (
    participantId: string,
    participantName: string,
    participantEmail: string,
    listingId?: string,
    listingTitle?: string
  ) => {
    setCreating(true);
    setError(null);

    try {
      const conversationId = await createConversation(
        participantId,
        participantName,
        participantEmail,
        listingId,
        listingTitle
      );
      return conversationId;
    } catch (err) {
      setError('Failed to create conversation');
      console.error('Error creating conversation:', err);
      return null;
    } finally {
      setCreating(false);
    }
  }, []);

  return {
    createConversation: createNewConversation,
    creating,
    error
  };
};

// Hook for real-time conversation updates
export const useConversationUpdates = (conversationId: string | null) => {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!conversationId) {
      setConversation(null);
      setLoading(false);
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setLoading(false);
      return;
    }

    // Subscribe to all conversations and filter for the specific one
    const unsubscribe = subscribeToConversations(
      currentUser.uid,
      (conversations) => {
        const targetConversation = conversations.find(c => c.id === conversationId);
        setConversation(targetConversation || null);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [conversationId]);

  return { conversation, loading };
};